// Ties each Office 365 sign-in popup to the login page that opened it. The
// login page passes a random value as MSAL's `state`; Microsoft returns it in
// the popup's URL fragment after MSAL's own state (`<library>|<ours>`), and
// main.ts only treats the window as that popup when the value is one this
// browser stored. A link cannot know the value, and concurrent sign-ins in
// other tabs use their own. window.opener cannot be used instead because
// Microsoft's sign-in pages clear it with Cross-Origin-Opener-Policy.

const keyPrefix = 'masar.msal-popup.';
const lifetimeMs = 10 * 60 * 1000;

/** Starts a sign-in: stores and returns the value to pass as MSAL `state`. */
export function startMsalPopup(): string {
    const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), b =>
        b.toString(16).padStart(2, '0')
    ).join('');
    try {
        removeExpired();
        localStorage.setItem(keyPrefix + nonce, String(Date.now()));
    } catch {
        // Storage unavailable: the popup falls back to booting the app.
    }
    return nonce;
}

export function endMsalPopup(nonce: string): void {
    try {
        localStorage.removeItem(keyPrefix + nonce);
    } catch {
        // Nothing to clear.
    }
}

/**
 * Returns the sign-in value when this window is the MSAL popup of a sign-in
 * this browser started in the last 10 minutes, otherwise null.
 */
export function returningMsalPopupNonce(): string | null {
    const response = new URLSearchParams(window.location.hash.slice(1));
    if (!response.has('code') && !response.has('error')) return null;

    try {
        const [libraryState, ...userState] = (
            response.get('state') ?? ''
        ).split('|');
        const nonce = userState.join('|');
        const startedAt = Number(localStorage.getItem(keyPrefix + nonce));
        if (!nonce || !startedAt || Date.now() - startedAt > lifetimeMs) {
            return null;
        }

        const base64 = libraryState.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        const isPopup =
            JSON.parse(atob(padded))?.meta?.interactionType === 'popup';
        return isPopup ? nonce : null;
    } catch {
        return null;
    }
}

function removeExpired(): void {
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key?.startsWith(keyPrefix)) continue;
        if (Date.now() - Number(localStorage.getItem(key)) > lifetimeMs) {
            localStorage.removeItem(key);
        }
    }
}
